/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';
import { Timestamp } from 'firebase/firestore';

import Part, { partSchema } from './part.js';

export const toolSchema = partSchema.shape({
  cavities: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .required('Cavity name is required')
        .test('unique-cavity-name', 'Cavity names must be unique', (value, context) => {
          if (!value) return true;
          const { options } = context; // Get field info
          const cavities = options.context?.cavities || []; // Get all cavities (using context)
          const duplicateCount = cavities.filter((cavity) => cavity.name === value).length;
          return duplicateCount <= 1; // If count > 1, it's a duplicate
        }),
    }),
  ).min(1, 'At least one cavity is required'),
  instance: Yup.number()
    .required('Instance is required'),
});

/**
 * @typedef {Yup.InferType<typeof toolSchema>} ToolSchema
 * @typedef {BaseDocData & ToolSchema & { createdAt: Timestamp; updatedAt: Timestamp; }} ToolDocData
*/

/**
 * Class representing a tool.
 * @class
 * @extends Part
 */
export default class Tool extends Part {
  /** @type {ToolDocData["cavities"]} */ cavities;
  /** @type {ToolDocData["instance"]} */ instance;

  /**
   * Create a tool.
   * @param {any} param0 - Raw Firestore document.
   * @throws {Yup.ValidationError} Raw Firestore document failed Yup validation.
   */
  constructor({ cavities, instance, ...props }) {
    const validatedData = toolSchema.validateSync(
      { cavities, instance, ...props },
      { context: { cavities } }, // Pass cavities to context for validation
    );

    super(validatedData);

    this.cavities = validatedData.cavities;
    this.instance = validatedData.instance;
  }

  /**
   * Converts the {@link Tool} instance to Firestore document data.
   * @returns {Omit<ToolDocData, 'id'>} {@link ToolDocData} (without `id`).
   */
  toFirestore({ serverTimestamp }) {
    const baseDoc = super.toFirestore({ serverTimestamp });
    return {
      ...baseDoc,
      instance: this.instance,
      metadata: {
        ...baseDoc.metadata,
        cavities: this.cavities,
      },
    };
  }

  /**
   * Creates a {@link Tool} instance from Firestore data.
   * @param {any} docData - Raw Firestore document.
   * @returns A {@link Tool} instance.
   * @throws {Yup.ValidationError} Raw Firestore document failed Yup validation.
   */
  static fromFirestore(docData) {
    const {
      cavities,
      linkedParts,
      components,
      ...restMetadata
    } = docData.metadata || {};
    return new Tool({
      ...docData,
      cavities,
      linkedParts,
      components,
      metadata: restMetadata || {},
    });
  }
}
